import { Loader2 } from "lucide-react";
import { useMemo } from "react";

import { Timeline, type ViewState } from "~/components/timeline";
import { useChunkedTimelineLoader } from "~/features/relations/hooks/useChunkedTimelineLoader";
import type { RelationListFilters } from "~/features/relations/types";
import { buildRelationsWhere } from "~/features/relations/utils/transformers";
import { useTenant } from "~/features/tenant/context";

interface RelationsTimelineViewProps {
  filters: RelationListFilters;
  onSelect: (id: string) => void;
  initialViewState?: ViewState;
  onViewStateChange?: (centerYear: number, scale: number) => void;
}

export function RelationsTimelineView({
  filters,
  onSelect,
  initialViewState,
  onViewStateChange,
}: RelationsTimelineViewProps) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  // Restrict to relation records; the chunked loader adds per-chunk dateJdn bounds on top.
  const baseWhere = useMemo(() => ({ ...buildRelationsWhere(filters), relationRecord: {} }), [filters]);
  const filterHash = useMemo(() => JSON.stringify(baseWhere), [baseWhere]);

  const { entities, loading, onVisibleYearRangeChange } = useChunkedTimelineLoader({
    tenantId,
    baseWhere,
    filterHash,
    orderBy: { date: "ASC" },
    gdtfFrom: filters.gdtfFrom,
    gdtfTo: filters.gdtfTo,
  });

  return (
    <div className="relative rounded-[var(--radius-md)] border border-border-default bg-white">
      {loading && (
        <Loader2 className="absolute right-2 top-2 z-10 h-4 w-4 animate-spin text-fg-3" aria-label="loading" />
      )}
      <Timeline
        entities={entities}
        height={600}
        storageKey="v2-relations-list"
        onEntityClick={(entity) => onSelect(entity.id)}
        onVisibleYearRangeChange={onVisibleYearRangeChange}
        initialViewState={initialViewState}
        onViewStateChange={onViewStateChange}
      />
    </div>
  );
}
